import { Chip, Tooltip, Box } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useTheme } from "@mui/material/styles";
import { CheckStatus, CheckStatusLabel } from "../../utils/enums";

export const CheckStatusChip = ({ status, count, isRefreshing = false, dense = false, iconOnly = false }) => {
  const theme = useTheme();

  const getStatusConfig = (status) => {
    if (status === CheckStatus?.PASS) {
      return {
        label: CheckStatusLabel.PASSING,
        color: theme.customColors.status.success.text,
        bgColor: theme.customColors.status.success.bg,
        icon: <CheckCircleOutlineIcon fontSize="small" />,
      };
    } else if (status === CheckStatus?.FAIL) {
      return {
        label: CheckStatusLabel.FAILING,
        color: theme.customColors.status.error.text,
        bgColor: theme.customColors.status.error.bg,
        icon: <ErrorOutlineIcon fontSize="small" />,
      };
    } else if (status === CheckStatus?.DISABLED) {
      return {
        label: CheckStatusLabel.DISABLED,
        color: theme.customColors.status.warning.text,
        bgColor: theme.customColors.status.warning.bg,
        icon: <BlockIcon fontSize="small" />,
      };
    }
    return {
      label: CheckStatusLabel.PENDING,
      color: theme.customColors.status.info.text,
      bgColor: theme.customColors.status.info.bg,
      icon: <ScheduleIcon fontSize="small" />,
    };
  };

  const config = getStatusConfig(status);

  // Compact, space-saving variant for the narrow Assets check columns: render
  // only the status icon (colored), with the full label ("Passing"/"Failing"/…)
  // available on hover. Being a fixed-size icon, it never overflows its cell, so
  // it can't trigger the cell's text-overflow ellipsis (the stray dot).
  if (iconOnly) {
    return (
      <Tooltip title={count > 0 ? `${count} ${config.label}` : config.label} arrow>
        <Box
          component="span"
          aria-label={config.label}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: config.color,
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        >
          {config.icon}
        </Box>
      </Tooltip>
    );
  }

  const shimmerStyles = isRefreshing
    ? {
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background: `linear-gradient(
            90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.5) 45%,
            rgba(255, 255, 255, 0.8) 50%,
            rgba(255, 255, 255, 0.5) 55%,
            transparent 100%
          )`,
          animation: "shimmerSweep 1.2s ease-in-out infinite",
          "@keyframes shimmerSweep": {
            "0%": {
              left: "-100%",
            },
            "100%": {
              left: "100%",
            },
          },
        },
      }
    : {};

  return (
    <Chip
      label={`${count > 0 ? `${count}` : ""} ${config.label}`}
      disabled={isRefreshing}
      sx={{
        backgroundColor: config.bgColor,
        color: config.color,
        // Dense mode (narrow Assets check columns): the chip fills its cell so
        // every chip in the row is the same width — symmetrical, like the
        // People table — and, since it exactly fits the cell, it can't overflow
        // and trigger the cell's text-overflow ellipsis (the stray dot).
        minWidth: dense ? 0 : "90px",
        width: dense ? "100%" : undefined,
        maxWidth: dense ? "104px" : "100%",
        textAlign: "center",
        borderRadius: 0,
        fontWeight: 600,
        fontSize: dense ? "0.7rem" : "0.75rem",
        border: "none",
        position: "relative",
        "& .MuiChip-label": dense
          ? {
              width: "100%",
              // 4px rather than 6px: at narrow table widths those 4px are the
              // difference between "Passing" and "Passin…".
              paddingLeft: "4px",
              paddingRight: "4px",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }
          : {},
        "& .MuiChip-icon": {
          color: config.color,
          marginLeft: "8px",
        },
        "&.Mui-disabled": {
          opacity: 0.85,
        },
        ...shimmerStyles,
      }}
    />
  );
};
